import { toast } from '@bozic/ui/toast';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { serializeDate } from '@/utils/dateFormatter';

import insuranceAPI from '../../../services/insuranceService';
import {
  getQuarter,
  getSalesEffectiveDate,
  getState
} from '../../dairyRevenueProtection/selectors';
import { loadersActions } from '../../loaders/actions';
import { scenarioAnalysisActions } from '../actions';
import { getScenarioWhatIfPrices } from '../selectors';

import mapScenarioAnalysisWhatIfDto from './mapScenarioAnalysisWhatIfDto';
import mapScenarioAnalysisWhatIfPayload from './mapScenarioAnalysisWhatIfPayload';
import updateWhatIfPrices from './updateWhatIfPrices';

import type ScenarioAnalysisWhatIfPayload from '../../../types/ScenarioWhatIf/ScenarioAnalysisWhatIfPayload';
import type { LegacySagaGenerator } from '../../legacySagaGenerator';
import type { AxiosResponse } from 'axios';
import type { ActionType } from 'typesafe-actions';

function* updateScenarioAnalysisWhatIfPrices(
  action: ActionType<typeof scenarioAnalysisActions.updateScenarioWhatIfPrices>
): LegacySagaGenerator {
  yield put(loadersActions.scenarioAnalysis.activate());
  const allWhatIfPrices = yield select(getScenarioWhatIfPrices);

  try {
    const whatIfOld = mapScenarioAnalysisWhatIfPayload(action.payload.whatIfOld);
    const whatIfNew = mapScenarioAnalysisWhatIfPayload(action.payload.whatIfNew);
    const stateCode: number = yield select(getState);
    const practiceCode: number = yield select(getQuarter);
    const salesEffectiveDate: Date = yield select(getSalesEffectiveDate);
    const whatIfPrices: AxiosResponse<ScenarioAnalysisWhatIfPayload> = yield call(
      insuranceAPI.updateScenarioAnalysisWhatIf,
      {
        whatIfOld,
        whatIfNew,
        stateCode: String(stateCode).padStart(2, '0'),
        practiceCode: String(practiceCode),
        salesEffectiveDate: serializeDate(salesEffectiveDate)
      }
    );
    const mappedPrices = mapScenarioAnalysisWhatIfDto(
      whatIfPrices.data,
      action.payload.reinsuranceYear
    );
    const newPrices = updateWhatIfPrices(allWhatIfPrices, mappedPrices);
    yield put(scenarioAnalysisActions.saveScenarioWhatIfPrices(newPrices));
  } catch (e) {
    console.error(e);
    toast.error('Failed to update what if prices');
  } finally {
    yield put(scenarioAnalysisActions.setRunScenarioButtonState(true));
    yield put(loadersActions.scenarioAnalysis.disable());
  }
}

function* scenarioAnalysisWhatIfSagaWatcher() {
  yield all([
    takeLatest(
      scenarioAnalysisActions.updateScenarioWhatIfPrices,
      updateScenarioAnalysisWhatIfPrices
    )
  ]);
}

export default scenarioAnalysisWhatIfSagaWatcher;
